import {FC, useEffect, useRef, useState} from 'react'

type AppointmentActionsDropdownProps = {
  showEditActions: boolean
  showCancelActions: boolean
  showAddNoteAction: boolean
  showViewClientNotesAction: boolean
  showRebroadcastAction: boolean
  isRecurring: boolean
  editSeriesDisabled: boolean
  onEditOccurrence: () => void
  onEditSeries: () => void
  onCancelOccurrence: () => void
  onCancelSeries: () => void
  onAddNote: () => void
  onViewClientNotes: () => void
  onRebroadcast: () => void
}

const AppointmentActionsDropdown: FC<AppointmentActionsDropdownProps> = ({
  showEditActions,
  showCancelActions,
  showAddNoteAction,
  showViewClientNotesAction,
  showRebroadcastAction,
  isRecurring,
  editSeriesDisabled,
  onEditOccurrence,
  onEditSeries,
  onCancelOccurrence,
  onCancelSeries,
  onAddNote,
  onViewClientNotes,
  onRebroadcast,
}) => {
  const [actionsOpen, setActionsOpen] = useState(false)
  const [editSubOpen, setEditSubOpen] = useState(false)
  const [cancelSubOpen, setCancelSubOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setActionsOpen(false)
        setEditSubOpen(false)
        setCancelSubOpen(false)
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  useEffect(() => {
    if (!actionsOpen) return
    const onDocClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setActionsOpen(false)
        setEditSubOpen(false)
        setCancelSubOpen(false)
      }
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [actionsOpen])

  const closeAll = () => {
    setActionsOpen(false)
    setEditSubOpen(false)
    setCancelSubOpen(false)
  }

  const menuItemStyle = {backgroundColor: 'transparent', color: '#3f4254', padding: '9px 12px'}

  const handleMenuItemMouseEnter = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.currentTarget.style.backgroundColor = '#f5f8fa'
    setEditSubOpen(false)
    setCancelSubOpen(false)
  }

  const handleMenuItemMouseLeave = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.currentTarget.style.backgroundColor = 'transparent'
  }

  return (
    <div className='d-flex flex-center mt-2 position-relative' ref={containerRef}>
      <button
        type='button'
        className='btn btn-sm d-inline-flex align-items-center gap-2 fw-semibold'
        style={{backgroundColor: '#a3a242', color: '#ffffff', border: 'none'}}
        onClick={() => {
          setActionsOpen((v) => !v)
          setEditSubOpen(false)
          setCancelSubOpen(false)
        }}
      >
        <i className='bi bi-gear-fill' style={{fontSize: '12px', color: '#ffffff'}}></i>
        Actions
        <i className='bi bi-chevron-down' style={{fontSize: '10px', color: '#ffffff'}}></i>
      </button>

      {actionsOpen && (
        <div
          className='position-absolute bg-white rounded shadow-sm border'
          style={{
            top: 'calc(100% + 6px)',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 60,
            width: '210px',
            padding: '6px',
          }}
        >
          {showEditActions && (
            <div
              className='position-relative'
              onMouseEnter={() => {
                setEditSubOpen(true)
                setCancelSubOpen(false)
              }}
              onMouseLeave={() => setEditSubOpen(false)}
            >
              <button
                type='button'
                className='w-100 btn btn-sm border-0 d-flex align-items-center justify-content-between fw-medium'
                style={{
                  backgroundColor: editSubOpen ? '#f5f8fa' : 'transparent',
                  color: '#3f4254',
                  padding: '9px 12px',
                }}
              >
                <span className='d-inline-flex align-items-center gap-2'>
                  <i className='bi bi-pencil-square' style={{color: '#a3a242'}}></i>
                  Edit
                </span>
                <i className='bi bi-chevron-right' style={{fontSize: '10px', color: '#a1a5b7'}}></i>
              </button>
              {editSubOpen && (
                <div
                  className='position-absolute bg-white rounded shadow-sm border'
                  style={{
                    top: '-6px',
                    left: 'calc(100% + 4px)',
                    zIndex: 61,
                    width: '190px',
                    padding: '6px',
                  }}
                >
                  <button
                    type='button'
                    className='w-100 btn btn-sm btn-light border-0 d-flex align-items-center gap-2 text-start fw-medium'
                    onClick={() => {
                      closeAll()
                      onEditOccurrence()
                    }}
                  >
                    <i className='bi bi-calendar-event' style={{color: '#a3a242'}}></i>
                    Edit Occurrence
                  </button>
                  <button
                    type='button'
                    className='w-100 btn btn-sm btn-light border-0 d-flex align-items-center gap-2 text-start fw-medium'
                    disabled={editSeriesDisabled}
                    title={editSeriesDisabled ? 'Only open assignments can be edited in series' : undefined}
                    onClick={() => {
                      if (editSeriesDisabled) return
                      closeAll()
                      onEditSeries()
                    }}
                  >
                    <i className='bi bi-collection' style={{color: '#a3a242'}}></i>
                    Edit Series
                  </button>
                </div>
              )}
            </div>
          )}

          {showCancelActions && (
            <div
              className='position-relative'
              onMouseEnter={() => {
                setCancelSubOpen(true)
                setEditSubOpen(false)
              }}
              onMouseLeave={() => setCancelSubOpen(false)}
            >
              <button
                type='button'
                className='w-100 btn btn-sm border-0 d-flex align-items-center justify-content-between fw-medium'
                style={{
                  backgroundColor: cancelSubOpen ? '#fff5f8' : 'transparent',
                  color: '#d7383c',
                  padding: '9px 12px',
                }}
                onClick={() => {
                  if (!isRecurring) {
                    closeAll()
                    onCancelOccurrence()
                  }
                }}
              >
                <span className='d-inline-flex align-items-center gap-2'>
                  <i className='bi bi-x-circle'></i>
                  Cancel
                </span>
                {isRecurring && (
                  <i className='bi bi-chevron-right' style={{fontSize: '10px', color: '#d7a0a1'}}></i>
                )}
              </button>
              {isRecurring && cancelSubOpen && (
                <div
                  className='position-absolute bg-white rounded shadow-sm border'
                  style={{
                    top: '-6px',
                    left: 'calc(100% + 4px)',
                    zIndex: 61,
                    width: '200px',
                    padding: '6px',
                  }}
                >
                  <button
                    type='button'
                    className='w-100 btn btn-sm border-0 d-flex align-items-center gap-2 text-start fw-medium'
                    style={{color: '#d7383c', padding: '9px 12px'}}
                    onClick={() => {
                      closeAll()
                      onCancelSeries()
                    }}
                  >
                    <i className='bi bi-collection'></i>
                    Cancel Series
                  </button>
                  <button
                    type='button'
                    className='w-100 btn btn-sm border-0 d-flex align-items-center gap-2 text-start fw-medium'
                    style={{color: '#d7383c', padding: '9px 12px'}}
                    onClick={() => {
                      closeAll()
                      onCancelOccurrence()
                    }}
                  >
                    <i className='bi bi-calendar-x'></i>
                    Cancel Occurrence
                  </button>
                </div>
              )}
            </div>
          )}

          {showAddNoteAction && (
            <button
              type='button'
              className='w-100 btn btn-sm border-0 d-flex align-items-center fw-medium'
              style={menuItemStyle}
              onMouseEnter={handleMenuItemMouseEnter}
              onMouseLeave={handleMenuItemMouseLeave}
              onClick={() => {
                closeAll()
                onAddNote()
              }}
            >
              <span className='d-inline-flex align-items-center gap-2'>
                <i className='bi bi-sticky' style={{color: '#a3a242'}}></i>
                Add a Note
              </span>
            </button>
          )}

          {showViewClientNotesAction && (
            <button
              type='button'
              className='w-100 btn btn-sm border-0 d-flex align-items-center fw-medium'
              style={menuItemStyle}
              onMouseEnter={handleMenuItemMouseEnter}
              onMouseLeave={handleMenuItemMouseLeave}
              onClick={() => {
                closeAll()
                onViewClientNotes()
              }}
            >
              <span className='d-inline-flex align-items-center gap-2'>
                <i className='bi bi-journal-text' style={{color: '#a3a242'}}></i>
                View Client Notes
              </span>
            </button>
          )}

          {showRebroadcastAction && (
            <>
              <div className='border-top my-1 mx-2'></div>
              <button
                type='button'
                className='w-100 btn btn-sm border-0 d-flex align-items-center fw-semibold'
                style={menuItemStyle}
                onMouseEnter={handleMenuItemMouseEnter}
                onMouseLeave={handleMenuItemMouseLeave}
                onClick={() => {
                  closeAll()
                  onRebroadcast()
                }}
              >
                <span className='d-inline-flex align-items-center gap-2'>
                  <i className='bi bi-broadcast-pin' style={{color: '#a3a242'}}></i>
                  Rebroadcast
                </span>
              </button>
            </>
          )}
        </div>
      )}
    </div>
  )
}

export default AppointmentActionsDropdown
